// src/pages/Users/Techniques/utils/techniquesUtils.js

export const DEFAULT_TECHNIQUES_STATE = {
  search: "",
  grade: "Toutes les ceintures",
  family: "Toutes les familles",
  uv2: "Toutes les fiches",
  showFav: false,
  sort: "beltFamily",
  perPage: 24,
};

export const FALLBACK_TECHNIQUE_IMG = "/img/no-image.jpg";

export const BELT_BASE = [
  "blanche",
  "jaune",
  "orange",
  "verte",
  "bleue",
  "marron",
  "noire",
];

export const FAMILY_ORDER = [
  "Jambe",
  "Hanche",
  "Epaule",
  "Bras",
  "Sutemi Waza",
  "Immobilisation",
  "Shime Waza",
  "Kansetsu-Waza",
  "Self Défense",
  "Kata",
  "Vocabulaire",
  "Les Bases",
  "Ukemi",
];

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

/**
 * Sécurisé contre :
 * - null
 * - undefined
 * - nombres
 * - booléens
 * - objets inattendus
 */
export function strip(value = "") {
  if (value === null || value === undefined) return "";

  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function cleanTechniqueText(value = "") {
  if (value === null || value === undefined) return "";

  return String(value)
    .replace(/^"+|"+$/g, "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function truncateText(value = "", maxLength = 120) {
  const clean = cleanTechniqueText(value);

  if (!clean) return "";

  if (clean.length <= maxLength) return clean;

  return `${clean.slice(0, maxLength).trim()}…`;
}

export function beltRank(ceinture = "") {
  const normalized = strip(ceinture);

  if (!normalized) return Number.POSITIVE_INFINITY;

  const danMatch = normalized.match(/(\d+)\s*(?:er|e)?\s*dan/);

  if (danMatch) {
    const danNumber = parseInt(danMatch[1], 10) || 1;
    return 6 + danNumber;
  }

  const colorsFound = BELT_BASE.map((color, index) =>
    normalized.includes(color) ? index : -1,
  ).filter((index) => index >= 0);

  if (colorsFound.length === 1) return colorsFound[0];

  if (colorsFound.length >= 2) {
    return (
      colorsFound.reduce((sum, value) => sum + value, 0) / colorsFound.length
    );
  }

  const exactIndex = BELT_BASE.indexOf(normalized);

  if (exactIndex >= 0) return exactIndex;

  return Number.POSITIVE_INFINITY;
}

export function familyRank(famille = "") {
  const normalized = strip(famille);

  if (!normalized) return 999;

  const index = FAMILY_ORDER.findIndex((item) => strip(item) === normalized);

  return index >= 0 ? index : 999;
}

export function normalizeUV2(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => String(item).trim());
  }

  return String(value)
    .split(/[,\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function buildTechniqueOptions(allTechniques = []) {
  const safeTechniques = Array.isArray(allTechniques)
    ? allTechniques.filter(Boolean)
    : [];

  const uniqueGrades = Array.from(
    new Set(
      safeTechniques
        .map((item) => item?.ceinture)
        .filter(
          (value) => value !== null && value !== undefined && value !== "",
        ),
    ),
  );

  uniqueGrades.sort((a, b) => beltRank(a) - beltRank(b));

  const families = Array.from(
    new Set(
      safeTechniques
        .map((item) => item?.famille)
        .filter(
          (value) => value !== null && value !== undefined && value !== "",
        ),
    ),
  ).sort((a, b) => familyRank(a) - familyRank(b) || collatorFR.compare(a, b));

  const uv2 = Array.from(
    new Set(
      safeTechniques.flatMap((item) => normalizeUV2(item?.uv2)).filter(Boolean),
    ),
  ).sort((a, b) => collatorFR.compare(a, b));

  return {
    grades: ["Toutes les ceintures", ...uniqueGrades],
    families: ["Toutes les familles", ...families],
    uv2: ["Toutes les fiches", ...uv2],
  };
}

export function filterAndSortTechniques(
  allTechniques = [],
  ui,
  favorites = [],
) {
  const safeTechniques = Array.isArray(allTechniques)
    ? allTechniques.filter(Boolean)
    : [];

  const safeUi = {
    ...DEFAULT_TECHNIQUES_STATE,
    ...(ui || {}),
  };

  const term = strip(safeUi.search);

  const filtered = safeTechniques.filter((technique) => {
    const searchHaystack = [
      technique?.nom,
      technique?.traduction,
      technique?.famille,
      technique?.ceinture,
      technique?.information,
    ]
      .map((value) => strip(value))
      .join(" ");

    const matchSearch = !term || searchHaystack.includes(term);

    const matchGrade =
      safeUi.grade === DEFAULT_TECHNIQUES_STATE.grade ||
      technique?.ceinture === safeUi.grade;

    const matchFamily =
      safeUi.family === DEFAULT_TECHNIQUES_STATE.family ||
      technique?.famille === safeUi.family;

    const matchUV2 =
      safeUi.uv2 === DEFAULT_TECHNIQUES_STATE.uv2 ||
      normalizeUV2(technique?.uv2).includes(safeUi.uv2);

    const matchFavorite = !safeUi.showFav || favorites.includes(technique?.id);

    return (
      matchSearch && matchGrade && matchFamily && matchUV2 && matchFavorite
    );
  });

  filtered.sort((a, b) => {
    const nameA = String(a?.nom || "");
    const nameB = String(b?.nom || "");

    if (safeUi.sort === "alpha") {
      return collatorFR.compare(nameA, nameB);
    }

    if (safeUi.sort === "grade") {
      return (
        beltRank(a?.ceinture) - beltRank(b?.ceinture) ||
        collatorFR.compare(nameA, nameB)
      );
    }

    if (safeUi.sort === "family") {
      return (
        familyRank(a?.famille) - familyRank(b?.famille) ||
        collatorFR.compare(nameA, nameB)
      );
    }

    const beltDiff = beltRank(a?.ceinture) - beltRank(b?.ceinture);

    if (beltDiff !== 0) return beltDiff;

    const familyDiff = familyRank(a?.famille) - familyRank(b?.famille);

    if (familyDiff !== 0) return familyDiff;

    return collatorFR.compare(nameA, nameB);
  });

  return filtered;
}

export function hasActiveFilters(ui) {
  const safeUi = {
    ...DEFAULT_TECHNIQUES_STATE,
    ...(ui || {}),
  };

  return (
    Boolean(safeUi.search) ||
    safeUi.grade !== DEFAULT_TECHNIQUES_STATE.grade ||
    safeUi.family !== DEFAULT_TECHNIQUES_STATE.family ||
    safeUi.uv2 !== DEFAULT_TECHNIQUES_STATE.uv2 ||
    safeUi.showFav ||
    safeUi.sort !== DEFAULT_TECHNIQUES_STATE.sort
  );
}

export function buildShareUrl(ui) {
  const safeUi = {
    ...DEFAULT_TECHNIQUES_STATE,
    ...(ui || {}),
  };

  const params = new URLSearchParams();

  if (safeUi.search) params.set("q", safeUi.search);

  if (safeUi.grade !== DEFAULT_TECHNIQUES_STATE.grade) {
    params.set("grade", safeUi.grade);
  }

  if (safeUi.family !== DEFAULT_TECHNIQUES_STATE.family) {
    params.set("famille", safeUi.family);
  }

  if (safeUi.uv2 !== DEFAULT_TECHNIQUES_STATE.uv2) {
    params.set("uv2", safeUi.uv2);
  }

  if (safeUi.showFav) {
    params.set("fav", "1");
  }

  if (safeUi.sort !== DEFAULT_TECHNIQUES_STATE.sort) {
    params.set("sort", safeUi.sort);
  }

  const query = params.toString();

  return `${window.location.origin}${window.location.pathname}${
    query ? `?${query}` : ""
  }`;
}

export function beltTheme(ceinture = "") {
  const normalized = strip(ceinture);

  if (normalized.includes("jaune")) {
    return {
      badge: "bg-yellow-300 text-yellow-950 ring-yellow-500/20",
      dot: "bg-yellow-400",
      gradient: "from-yellow-500 via-amber-500 to-orange-400",
    };
  }

  if (normalized.includes("orange")) {
    return {
      badge: "bg-orange-300 text-orange-950 ring-orange-500/20",
      dot: "bg-orange-400",
      gradient: "from-orange-600 via-orange-500 to-amber-400",
    };
  }

  if (normalized.includes("verte")) {
    return {
      badge: "bg-emerald-400 text-emerald-950 ring-emerald-500/20",
      dot: "bg-emerald-400",
      gradient: "from-emerald-700 via-emerald-600 to-emerald-400",
    };
  }

  if (normalized.includes("bleue")) {
    return {
      badge: "bg-blue-500 text-white ring-blue-500/20",
      dot: "bg-blue-500",
      gradient: "from-blue-900 via-blue-700 to-blue-500",
    };
  }

  if (normalized.includes("marron")) {
    return {
      badge: "bg-amber-900 text-white ring-amber-800/30",
      dot: "bg-amber-900",
      gradient: "from-amber-950 via-amber-900 to-amber-700",
    };
  }

  if (normalized.includes("noire") || normalized.includes("dan")) {
    return {
      badge: "bg-neutral-950 text-white ring-neutral-700/30",
      dot: "bg-neutral-950",
      gradient: "from-neutral-950 via-neutral-800 to-neutral-600",
    };
  }

  return {
    badge: "bg-slate-200 text-slate-800 ring-slate-300",
    dot: "bg-slate-400",
    gradient: "from-slate-800 via-slate-700 to-slate-500",
  };
}
